import React, { useState } from 'react';
import styled from 'styled-components';
import SEO from '../components/SEO';
import { submitContactForm } from '../utils/formSubmission';

const PageContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 1.5rem 2rem 0.5rem;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 3px solid var(--primary-color);
`;

const PageDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #555;
  margin-bottom: 2rem;
`;

const ContactForm = styled.form`
  margin-top: 2rem;
  background: #f9f9f9;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #444;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.9rem 1rem;
  font-size: 1rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(255, 107, 0, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.9rem 1rem;
  font-size: 1rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  transition: all 0.3s ease;
  min-height: 150px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(255, 107, 0, 0.1);
  }
`;

const SubmitButton = styled.button`
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: #e05e00;
    transform: translateY(-2px);
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusMessage = styled.div<{ isError: boolean }>`
  margin-top: 1.5rem;
  padding: 1rem;
  border-radius: 6px;
  background-color: ${props => props.isError ? '#ffebee' : '#e8f5e9'};
  color: ${props => props.isError ? '#c62828' : '#2e7d32'};
  font-weight: 500;
`;

// Hidden honeypot field to prevent bot submissions
const HoneypotInput = styled.input`
  display: none;
`;

interface FormData {
  name: string;
  email: string;
  subject: string;
  message: string;
  // Honeypot field
  website: string;
}

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
    website: '' // Honeypot field
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    message: string;
    isError: boolean;
  } | null>(null);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Honeypot check - if this field is filled, it's probably a bot
    if (formData.website) {
      console.log('Bot detected');
      // Fake success to confuse bots
      setSubmitStatus({
        message: 'Thank you for your message! We will get back to you soon.',
        isError: false
      });
      return;
    }
    
    // Basic validation
    if (!formData.name || !formData.email || !formData.message) {
      setSubmitStatus({
        message: 'Please fill in all required fields.',
        isError: true
      });
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus({
        message: 'Please enter a valid email address.',
        isError: true
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      setSubmitStatus(null);
      
      // Use the existing form submission utility with the correct format
      const result = await submitContactForm({
        full_name: formData.name,
        email: formData.email,
        message: formData.message,
        service_type: formData.subject || 'Contact Form Submission'
      });
      
      if (result.success) {
        setSubmitStatus({
          message: 'Thank you for your message! We will get back to you soon.',
          isError: false
        });
        
        // Reset the form
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: '',
          website: ''
        });
      } else {
        setSubmitStatus({
          message: result.message || 'Something went wrong. Please try again later.',
          isError: true
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({
        message: 'Something went wrong. Please try again later.',
        isError: true
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <PageContainer className="font--jakarta">
      <SEO 
        title="Contact Us | Software Development Experts" 
        description="Get in touch with UPTREND Systems, Whitby's premier software development company. Discuss your custom software project, request a consultation, or ask about our services."
        keywords="contact software developer, Whitby software company contact, custom software inquiry, UPTREND Systems contact, software development consultation, Ontario tech company"
        canonicalUrl="/contact"
        pageType="contact"
      />
      
      <PageTitle>Contact Us</PageTitle>
      <PageDescription>
        Have a question, project idea, or just want to say hello? Fill out the form below and we'll get back to you as soon as possible.
      </PageDescription>
      
      <ContactForm onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="name">Name *</Label>
          <Input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Your name"
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="email">Email *</Label>
          <Input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="your.email@example.com"
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="subject">Subject</Label>
          <Input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleChange}
            placeholder="What's this about?"
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="message">Message *</Label>
          <TextArea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us about your project, question, or request..."
            required
          />
        </FormGroup>
        
        {/* Honeypot field to prevent spam */}
        <HoneypotInput
          type="text"
          name="website"
          value={formData.website}
          onChange={handleChange}
          autoComplete="off"
        />
        
        <SubmitButton type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Sending...' : 'Send Message'}
        </SubmitButton>
        
        {submitStatus && (
          <StatusMessage isError={submitStatus.isError}>
            {submitStatus.message}
          </StatusMessage>
        )}
      </ContactForm>
    </PageContainer>
  );
};

export default ContactPage; 